/**
 * The dashboard's one door to the backend: the public API, with the signed-in user's token.
 *
 * CLAUDE.md §4: "The dashboard consumes the public API. No privileged side doors." So this is a thin
 * `fetch` — the same requests a customer's own integration would make — carrying the Supabase
 * session's access token as a bearer, which the api Worker verifies against the project's JWKS
 * (`aud: authenticated`). Nothing here knows a table name.
 *
 * Every POST carries an `Idempotency-Key` (api-platform.md §3: honoured on every POST). A retry
 * after a dropped connection replays the stored answer instead of placing a second field.
 */
import { getClient } from '../supabase'
import type { UploadProgress } from './tus'
import type { FileTicketResponse } from './types'

// Read at call time, not import time: a build variable that is checked when the module loads cannot
// be stubbed in a test, and a module that captures it cannot notice it was never set.
const baseUrl = (): string | undefined =>
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '')

export function apiConfigured(): boolean {
  const base = baseUrl()
  return typeof base === 'string' && base.length > 0
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * The scalars the API carried alongside the code — a plan limit, a retry delay.
     *
     * They used to be dropped here, which made a refusal like `invite_batch_too_large` unusable:
     * the number the reader needs is in the body, and a screen that had to parse "2" back out of an
     * English sentence would break the moment the sentence improved. `apps/api/src/http.ts`'s
     * `problem()` is the other half — it puts them in `error`, never free text.
     */
    readonly details: Readonly<Record<string, string | number | boolean | null>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function bearer(): Promise<string | null> {
  const client = getClient()
  if (!client) return null
  const { data } = await client.auth.getSession()
  return data.session?.access_token ?? null
}

/**
 * `fetch`, with a transport failure turned into something a screen can say out loud.
 *
 * A `fetch` that never receives a response rejects with `TypeError: Failed to fetch` — five words
 * naming neither cause nor remedy, which every surface here rendered verbatim. A tenant reported
 * exactly that string on a document upload and it was unactionable in both directions: nobody could
 * tell whether it was their network, their file, or ours.
 *
 * The browser says it for a dropped connection, a DNS failure, a CORS refusal, AND for a response
 * the server really sent but the browser refused to hand over. That last case is the common one
 * here: OUR error responses carry CORS headers when the caller's origin is on `WEB_ORIGINS` (the
 * Worker wraps every one of them, `apps/api/src/index.ts`), so a 401 or a 500 of ours arrives
 * readable and never reaches this path at all. "Failed to fetch" on this API therefore usually
 * means the request arrived and the server did not finish, not that it never left.
 *
 * `status: 0` marks "no response at all", which no HTTP status can collide with. An `AbortError` is
 * re-thrown untouched: a caller that cancelled its own request is not looking at a failure.
 *
 * ## The paragraph above was right and the message under it was wrong
 *
 * That reasoning shipped, and then the string said "The connection dropped or the request took too
 * long" — the one thing the common case is NOT. Measured on 2026-09-08 against the production API:
 * a document upload from the dashboard got an HTTP **503** from the edge, recorded in the network
 * panel, while the app told the operator their connection had dropped. Two people can look at that
 * message and neither can act on it, because it names the half of the world that was working.
 *
 * ## And then the replacement was wrong in the same way, one step further along
 *
 * The fix for that told two cases apart and then asserted a CAUSE for one of them: "this is usually
 * a security or firewall rule on the API hostname". It is not usually anything of the sort. The
 * clause it skipped is the one above — our responses carry CORS headers *when the origin is
 * allowed* — so a console served from an address that is not on `WEB_ORIGINS` fails identically,
 * and that is the cheaper and likelier of the two. Production allows exactly ONE origin
 * (`apps/api/wrangler.toml`, `[env.production.vars]`); the `*.pages.dev` wildcards are staging's.
 *
 * The founder hit it on the Envelopes list on 2026-09-12 and the message named a firewall, which is
 * the one thing nobody could check from the console. So the three cases are now told apart instead
 * of guessed at, by `diagnoseNoResponse`, and none of them asserts a cause it did not observe.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const diagnosis = await diagnoseNoResponse(url)
    throw new ApiError(0, diagnosis.code, diagnosis.message)
  }
}

/**
 * How long the probes below may take IN TOTAL before they stop being worth waiting for.
 *
 * One budget shared across both, not one each: this sits on a path where somebody is already
 * watching a spinner, and a dead host must not cost twice as long to report as it did when there
 * was one probe. The second probe inherits whatever the first left, and takes zero if nothing.
 */
const PROBE_TIMEOUT_MS = 3000

/**
 * One probe of the public health route. `true` means a response arrived and the browser kept it.
 *
 * `/_health` is `surface: 'public'` (`apps/api/src/index.ts`) and touches no database, so it
 * answers for any caller and answers fast. A probe of the REQUEST path instead would re-send an
 * upload's bytes and could be refused for its own reasons, which is a different question.
 */
async function healthProbeKept(
  origin: string,
  mode: 'cors' | 'no-cors',
  deadline: number,
): Promise<boolean> {
  const remaining = deadline - Date.now()
  if (remaining <= 0) return false
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller === null ? null : setTimeout(() => controller.abort(), remaining)
  try {
    await fetch(`${origin}/_health`, {
      method: 'GET',
      mode,
      cache: 'no-store',
      ...(controller === null ? {} : { signal: controller.signal }),
    })
    return true
  } catch {
    return false
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}

/**
 * Which of the three no-response worlds we are in — decided by observation, never by assertion.
 *
 * `fetch` rejects identically for "nothing answered", "the host will not let this PAGE read
 * anything" and "the host let this page read, but not THIS request". Those have three different
 * owners and three different remedies, and the previous version collapsed the last two into one
 * message that named a firewall. Two probes of `/_health` separate them, because the two `mode`s
 * fail for different reasons:
 *
 *   * `cors` keeps the response only if the origin is on `WEB_ORIGINS` — it is a real, readable
 *     request, refused exactly where our own requests are refused.
 *   * `no-cors` resolves OPAQUELY for any HTTP response at all — a 403 block page, a 503, a
 *     challenge — because an opaque response carries no status to be refused over. It rejects only
 *     when nothing answered.
 *
 * So:
 *
 *   cors kept                → this page may read this API; the failure was request-shaped → `blocked`
 *   cors refused, no-cors ok → the host answers but grants this page nothing → `origin_refused`
 *   neither                  → nothing is answering at that origin → `unreachable`
 *
 * `origin_refused` is the honest name for the middle one: it is what was OBSERVED. It stays true
 * whether the cause is a console served from an unlisted address or a rule on the hostname
 * swallowing every reply, and it does not pick between them, because the browser is not allowed to
 * see which and a confident wrong cause is what this function has now got wrong twice.
 *
 * A probe that fails for its own reasons — a timeout, no `AbortController` — degrades toward
 * `unreachable`, the most conservative of the three: claiming a refusal we did not observe would be
 * the same mistake pointed the other way.
 */
async function diagnoseNoResponse(url: string): Promise<{ code: string; message: string }> {
  const unreachable = {
    code: 'unreachable',
    message: 'The server did not answer. The connection dropped or the request took too long.',
  }
  if (typeof fetch !== 'function') return unreachable

  let origin: string
  try {
    origin = new URL(url).origin
  } catch {
    return unreachable
  }

  const deadline = Date.now() + PROBE_TIMEOUT_MS

  if (await healthProbeKept(origin, 'cors', deadline)) {
    return {
      code: 'blocked',
      // The one thing this arm can say that the old message could not: it is NOT about who is
      // calling. The health route came back readable to this very page, so the origin is allowed
      // and the remedy is not a CORS list — it is whatever this route did.
      message:
        'The API host answered, and this page is allowed to read from it — the health route came ' +
        'back. So this request failed on its own, not because of who sent it. Nothing was saved.',
    }
  }

  if (await healthProbeKept(origin, 'no-cors', deadline)) {
    return {
      code: 'origin_refused',
      // Names both live causes and neither as fact. The address is the half the reader can check
      // without leaving the screen, so it goes first.
      message:
        'The API host answered but let this page read none of it, not even the health route, so ' +
        'it is refusing this page rather than this request. Nothing was saved. Either this ' +
        'console runs on an address the API does not allow, or a rule on the hostname is ' +
        'answering in its place.',
    }
  }

  return unreachable
}

export type ApiInit = {
  // PUT is here because the API has PUT routes and this union did not, so the dashboard could not
  // call them at all — a type error at the first attempt rather than a runtime surprise, but still
  // a client that could not reach part of its own API.
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  readonly body?: unknown
  readonly signal?: AbortSignal
  /**
   * An idempotency key chosen by the CALLER, for a POST whose retry must be the same attempt.
   *
   * Without this every POST mints a fresh key, which is right for a one-shot call and wrong for an
   * upload: a Retry after a failure part way through would present a new key, and the server would
   * treat it as a second upload rather than the same one. `apiUploadResumable` keeps one key for
   * the life of an upload entry for exactly that reason.
   */
  readonly idempotencyKey?: string
}

/** Fetch one resource. Throws `ApiError` with the API's own code on a non-2xx. */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  if (!apiConfigured()) throw new ApiError(0, 'not_configured', 'VITE_API_URL is not set')
  const method = init.method ?? 'GET'
  const headers: Record<string, string> = { accept: 'application/json' }
  const token = await bearer()
  if (token !== null) headers.authorization = `Bearer ${token}`
  if (init.body !== undefined) headers['content-type'] = 'application/json'
  if (method === 'POST') headers['idempotency-key'] = init.idempotencyKey ?? crypto.randomUUID()

  const response = await send(`${baseUrl()}${path}`, {
    method,
    headers,
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    ...(init.signal === undefined ? {} : { signal: init.signal }),
  })
  if (response.status === 204) return undefined as T
  const payload = (await response.json().catch(() => null)) as {
    error?: Record<string, string | number | boolean | null | undefined>
  } | null
  if (!response.ok) {
    const error = payload?.error ?? {}
    const details: Record<string, string | number | boolean | null> = {}
    for (const [key, value] of Object.entries(error)) {
      if (key === 'code' || key === 'message' || value === undefined) continue
      details[key] = value
    }
    const code = typeof error['code'] === 'string' ? error['code'] : 'error'
    // ADR 0106: a team switched social sign-in off and this session came through Microsoft. Every
    // request would answer the same 403, so the console signs the person out and sends them to the
    // login screen with the reason, where `MicrosoftButton` shows the API's own sentence. Once,
    // not per request: the sign-out is what stops the loop.
    if (response.status === 403 && code === 'social_login_disabled' && !leavingForSocialRefusal) {
      leavingForSocialRefusal = true
      void (getClient()?.auth.signOut() ?? Promise.resolve()).finally(() => {
        window.location.assign('/auth/login?refused=social_login_disabled')
      })
    }
    throw new ApiError(
      response.status,
      code,
      typeof error['message'] === 'string' ? error['message'] : response.statusText,
      details,
    )
  }
  return payload as T
}

let leavingForSocialRefusal = false

/**
 * Fetch one BINARY resource — the flattened preview, the one download the API returns as bytes
 * rather than as a signed URL (see the api Worker's routes/preview.ts). Same bearer, same error
 * shape; the body comes back as a Blob for the browser to save.
 *
 * `accept` defaults to the PDF that every caller wanted until the Reports CSV export, which asks
 * for `text/csv`: the header is what the request says it can take, and a client that announces
 * PDF while asking for a spreadsheet is telling the server two different things.
 */
export async function apiBlob(
  path: string,
  init: { readonly signal?: AbortSignal; readonly accept?: string } = {},
): Promise<Blob> {
  if (!apiConfigured()) throw new ApiError(0, 'not_configured', 'VITE_API_URL is not set')
  const headers: Record<string, string> = { accept: init.accept ?? 'application/pdf' }
  const token = await bearer()
  if (token !== null) headers.authorization = `Bearer ${token}`
  const response = await send(`${baseUrl()}${path}`, {
    method: 'GET',
    headers,
    ...(init.signal === undefined ? {} : { signal: init.signal }),
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string }
    } | null
    throw new ApiError(
      response.status,
      payload?.error?.code ?? 'error',
      payload?.error?.message ?? response.statusText,
    )
  }
  return response.blob()
}

/**
 * POST raw BYTES — one part, in the body, with no multipart wrapper.
 *
 * `apiUpload` below sends `multipart/form-data` because `/v1/files` takes a filename alongside the
 * bytes. A profile photo takes neither a filename nor any other field, so the multipart envelope
 * would be a parser on both ends carrying nothing.
 *
 * The `content-type` sent here is the browser's own guess from the `File`, and the server IGNORES
 * it — what the bytes are is decided by sniffing them, because a declared type is a claim by the
 * uploader rather than evidence. It is sent anyway so the request is not lying by omission.
 */
export async function apiBytes(path: string, body: Blob): Promise<void> {
  if (!apiConfigured()) throw new ApiError(0, 'not_configured', 'VITE_API_URL is not set')
  const headers: Record<string, string> = {
    accept: 'application/json',
    'idempotency-key': crypto.randomUUID(),
  }
  if (body.type !== '') headers['content-type'] = body.type
  const token = await bearer()
  if (token !== null) headers.authorization = `Bearer ${token}`

  const response = await send(`${baseUrl()}${path}`, { method: 'POST', headers, body })
  if (response.ok) return
  const payload = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string }
  } | null
  throw new ApiError(
    response.status,
    payload?.error?.code ?? 'error',
    payload?.error?.message ?? response.statusText,
  )
}

/**
 * Upload one file: `POST /v1/files` in the browser's multipart shape. The API's DIRECT path — the
 * bytes ride in the request, capped server-side; the resumable TUS ticket is the composer's later
 * work. No `content-type` header is set here: the browser writes the multipart boundary itself.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  /**
   * The SAME key for every attempt at one file, minted by the caller and kept for the life of the
   * upload entry.
   *
   * It used to be `crypto.randomUUID()` right here, which is a fresh key per CALL — so the Retry
   * button beside a failed upload was not a retry at all, it was a second upload. That matters
   * precisely in the case Retry exists for: a request that reached the server and succeeded but
   * whose response was lost. The first attempt stored a file, the retry stored another, and the
   * idempotency key that exists to prevent exactly that was the thing making it happen.
   */
  idempotencyKey: string,
): Promise<T> {
  if (!apiConfigured()) throw new ApiError(0, 'not_configured', 'VITE_API_URL is not set')
  const headers: Record<string, string> = {
    accept: 'application/json',
    'idempotency-key': idempotencyKey,
  }
  const token = await bearer()
  if (token !== null) headers.authorization = `Bearer ${token}`
  const form = new FormData()
  form.append('file', file, file.name)
  const response = await send(`${baseUrl()}${path}`, { method: 'POST', headers, body: form })
  const payload = (await response.json().catch(() => null)) as {
    error?: Record<string, string | number | boolean | null | undefined>
  } | null
  if (!response.ok) {
    const error = payload?.error ?? {}
    const details: Record<string, string | number | boolean | null> = {}
    for (const [key, value] of Object.entries(error)) {
      if (key === 'code' || key === 'message' || value === undefined) continue
      details[key] = value
    }
    throw new ApiError(
      response.status,
      typeof error['code'] === 'string' ? error['code'] : 'error',
      typeof error['message'] === 'string' ? error['message'] : response.statusText,
      details,
    )
  }
  return payload as T
}

/**
 * Upload a document the way that actually reaches storage.
 *
 * Three steps, and the middle one is the point: the bytes go from the browser straight to Supabase
 * Storage and never cross `sign-api.flowza.ai`, whose WAF refuses binary request bodies and takes
 * every real PDF with them.
 *
 *   1. `POST /v1/files` with `resumable`, which creates a `pending` row and returns a TUS ticket —
 *      the endpoint, the bucket, the object key, and an HMAC bound to all three.
 *   2. The TUS upload itself (`tus.ts`).
 *   3. `POST /v1/files/{id}/complete`, which hands the ticket back. The server checks the object is
 *      really there at the size Storage reports, that it is inside the prefix the ticket allowed,
 *      and that its first bytes are the kind of file the name claims — then marks the row uploaded.
 *
 * Step 3 is not bookkeeping. Until a row is `uploaded` it cannot be attached to a document, so an
 * upload that skipped it would leave a file the product can see and cannot use.
 *
 * The idempotency key belongs to the whole attempt, not to step 1: a Retry after a failure in step
 * 2 or 3 must reuse the same key so it resumes one upload rather than starting a second.
 */
export async function apiUploadResumable(
  file: File,
  idempotencyKey: string,
  options: {
    readonly onProgress?: (progress: UploadProgress) => void
    readonly signal?: AbortSignal
  } = {},
): Promise<FileTicketResponse> {
  if (!apiConfigured()) throw new ApiError(0, 'not_configured', 'VITE_API_URL is not set')

  const ticket = await api<FileTicketResponse>('/v1/files', {
    method: 'POST',
    body: {
      filename: file.name,
      content_type: file.type === '' ? 'application/octet-stream' : file.type,
      // `byte_size`, which is the name the API reads and the name the `File` resource answers with.
      // This said `size` for as long as the resumable path has existed, and the server has never
      // looked at a field by that name — so every upload from this app declared NO size, which had
      // two consequences and neither was visible from here. The plan's `documents.max_bytes` check
      // was skipped, because it only runs on a declared size. And the ticket fell back to the
      // DIRECT-upload cap of 6 MB, so `POST /v1/files/{id}/complete` refused anything bigger with
      // `upload_token_rejected` — after the bytes had already been uploaded in full.
      byte_size: file.size,
      resumable: true,
    },
    idempotencyKey,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  })

  // A ticket missing any of its four parts cannot be uploaded against. Failing here names the
  // cause; letting `tus.ts` fail on an undefined endpoint would report a network problem.
  //
  // WHICH parts are missing is the whole diagnosis, so it goes in the message. Measured on
  // production 2026-09-10: every upload failed with the old, partless sentence, and the reason was
  // that `upload_bucket` and `upload_key` were absent — they were added to `POST /v1/files` in #161
  // and the deployment serving that request predated it (CLAUDE.md §7). The frontend and the Worker
  // do not ship together, so a change with a client half and a server half arrives in two pieces by
  // default. Naming the fields turns that from a browser investigation into one line of an error
  // message.
  const { upload_url: endpoint, upload_bucket: bucket, upload_key: objectKey } = ticket
  const token = ticket.upload_token
  const missing = (
    [
      ['upload_url', endpoint],
      ['upload_bucket', bucket],
      ['upload_key', objectKey],
      ['upload_token', token],
    ] as const
  )
    .filter(([, value]) => value == null)
    .map(([name]) => name)
  // Spelled out rather than `missing.length > 0`, because this `if` is what narrows the four away
  // from `string | null | undefined` for the `tusUpload` call below — a length check reads the
  // same and leaves them nullable, and `noUncheckedIndexedAccess` then rejects the call site.
  if (endpoint == null || bucket == null || objectKey == null || token == null) {
    throw new ApiError(
      0,
      'ticket_incomplete',
      `the server did not return a usable upload ticket — it is missing ${missing.join(', ')}. ` +
        `The API serving this request may be older than this app.`,
    )
  }

  // Loaded HERE rather than at module scope. `client.ts` is imported by every route including the
  // field editor, which never uploads anything — a static import put the TUS protocol into the
  // editor bundle and took its budget headroom from 9% to 5%. A dynamic import keeps it in the one
  // chunk that needs it, and this function is already async and already doing a round trip.
  const { TusError, tusUpload } = await import('./tus')

  try {
    await tusUpload({ endpoint, bucket, objectKey }, file, options)
  } catch (error) {
    // The TUS layer's own words: "your session may have expired", "the connection dropped". They
    // are about storage rather than about this API, and rewriting them as an ApiError would put a
    // status code on something that never had one.
    throw error instanceof TusError
      ? new ApiError(error.status ?? 0, error.code, error.message)
      : error
  }

  return api<FileTicketResponse>(`/v1/files/${ticket.id}/complete`, {
    method: 'POST',
    body: { upload_token: token },
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  })
}
